import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  FormControlLabel,
  Switch,
  Typography,
} from '@mui/material';

import Button from '@/components/Button';
import {
  AppBinaryVersionPin,
  AppBinaryVersionPlatform,
  createAppBinaryVersionPin,
  updateAppBinaryVersionPin,
} from '@/services/appBinaryVersionPinsAPI';

type Props = {
  open: boolean;
  pin: AppBinaryVersionPin | null;
  onClose: () => void;
  onSaved: () => void;
};

const PLATFORMS: AppBinaryVersionPlatform[] = ['IOS', 'ANDROID'];
const MAX_SUPPORTED_VERSION_COUNT = 20;

// Mirrors `AppBinaryVersionPin::VERSION_FORMAT` in graphy. Keep these in
// sync — the form does client-side preview validation so admins see the
// rejection before the round-trip, but the server is the authority.
const VERSION_PATTERN = /^\d+(\.\d+)*$/;

const parseVersions = (raw: string): string[] =>
  raw
    .split(/[\n,]/)
    .map((s) => s.trim())
    .filter((s) => s.length > 0);

const AppBinaryVersionPinModal = ({ open, pin, onClose, onSaved }: Props) => {
  const isEdit = !!pin;
  const [platform, setPlatform] = useState<AppBinaryVersionPlatform>('IOS');
  const [supportedVersionsText, setSupportedVersionsText] = useState('');
  const [gateEnabled, setGateEnabled] = useState<boolean>(true);
  const [notes, setNotes] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setPlatform(pin?.platform ?? 'IOS');
      setSupportedVersionsText((pin?.supported_versions ?? []).join('\n'));
      setGateEnabled(pin?.gate_enabled ?? true);
      setNotes(pin?.notes ?? '');
      setError(null);
    }
  }, [open, pin]);

  const parsedVersions = parseVersions(supportedVersionsText);
  const invalidVersions = parsedVersions.filter(
    (v) => !VERSION_PATTERN.test(v),
  );
  const overCap = parsedVersions.length > MAX_SUPPORTED_VERSION_COUNT;
  const clientSideError = (() => {
    if (invalidVersions.length > 0) {
      return `Invalid version format: ${invalidVersions
        .map((v) => `'${v}'`)
        .join(', ')}. Expected dotted-numeric like '2.2605.10'.`;
    }
    if (overCap) {
      return `Too many entries (${parsedVersions.length}). Max is ${MAX_SUPPORTED_VERSION_COUNT}.`;
    }
    return null;
  })();

  const handleSubmit = async () => {
    if (clientSideError) {
      setError(clientSideError);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      if (isEdit && pin) {
        await updateAppBinaryVersionPin(pin.id, {
          supported_versions: parsedVersions,
          gate_enabled: gateEnabled,
          notes: notes.trim() || null,
        });
      } else {
        await createAppBinaryVersionPin({
          platform,
          supported_versions: parsedVersions,
          gate_enabled: gateEnabled,
          notes: notes.trim() || null,
        });
      }
      onSaved();
      onClose();
    } catch (err: any) {
      setError(
        err.response?.data?.errors?.join(', ') ||
          err.message ||
          'Failed to save pin',
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        {isEdit ? 'Edit Binary Pin' : 'Create Binary Pin'}
      </DialogTitle>
      <DialogContent
        sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}
      >
        {error && <Alert severity="error">{error}</Alert>}

        <Alert severity="info" sx={{ mt: 1 }}>
          <Typography variant="body2">
            <strong>Publish to the App Store / Play Store FIRST</strong>, then
            add the new version string here. Reversing the order locks the
            existing fleet on the force-update screen while the Store still
            serves the previous (no-longer-allowed) binary.
          </Typography>
        </Alert>

        <FormControlLabel
          sx={{ mt: 1 }}
          control={
            <Switch
              checked={gateEnabled}
              onChange={(e) => setGateEnabled(e.target.checked)}
              color="primary"
            />
          }
          label={
            <Typography variant="body2">
              <strong>{gateEnabled ? 'Gate ON' : 'Gate OFF'}</strong> —{' '}
              {gateEnabled
                ? 'devices on an unlisted binary are sent to the Store'
                : 'all binaries skip enforcement (kill-switch / rollback)'}
            </Typography>
          }
        />

        <FormControl fullWidth size="small" disabled={isEdit}>
          <InputLabel>Platform</InputLabel>
          <Select
            value={platform}
            label="Platform"
            onChange={(e) =>
              setPlatform(e.target.value as AppBinaryVersionPlatform)
            }
            MenuProps={{ disablePortal: true }}
          >
            {PLATFORMS.map((p) => (
              <MenuItem key={p} value={p}>
                {p}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <TextField
          label="Supported binary versions (one per line)"
          placeholder={'2.2605.10\n2.2605.09'}
          value={supportedVersionsText}
          onChange={(e) => setSupportedVersionsText(e.target.value)}
          fullWidth
          size="small"
          multiline
          minRows={4}
          error={!!clientSideError}
          helperText={
            clientSideError ??
            `Exact-match allow-list. ${parsedVersions.length} of ${MAX_SUPPORTED_VERSION_COUNT} entries. Devices on any of these binaries are accepted; everything else is sent to the Store.`
          }
        />

        <TextField
          label="Notes (optional)"
          placeholder="e.g. dropped 2.2605.08 after fleet rolled forward"
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          fullWidth
          size="small"
          multiline
          minRows={2}
        />
      </DialogContent>
      <DialogActions>
        <Button
          a11yLabel="Cancel"
          label="Cancel"
          appearance="plain"
          onClick={onClose}
        />
        <Button
          a11yLabel={isEdit ? 'Save pin' : 'Create pin'}
          label={isEdit ? 'Save' : 'Create'}
          isLoading={loading}
          onClick={handleSubmit}
        />
      </DialogActions>
    </Dialog>
  );
};

export default AppBinaryVersionPinModal;
